import { useEffect, useState, useCallback } from "react";
import { RefreshCw, Play, KeyRound, AlertTriangle, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Skeleton } from "../components/ui/skeleton";
import AdminConfig from "./AdminConfig";
import AdminEmail from "./AdminEmail";
import AdminJira from "./AdminJira";
import AdminGate from "./AdminGate";
import AdminProjects from "./AdminProjects";

function inviteToast(t, invite) {
  if (!invite) return;
  if (invite.status === "SENT") toast.success(t("inviteSent"));
  else if (invite.status === "SIMULATED") toast.info(t("inviteSimulated"));
  else toast.error(`${t("inviteFailed")}: ${invite.error || ""}`);
}

function ResetPasswordDialog({ target, onClose, withInvite }) {
  const { t } = useLang();
  const [pw, setPw] = useState("");
  const [invite, setInvite] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setInvite(!!withInvite); setPw(""); }, [target, withInvite]);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${target.id}/reset-password`, { new_password: pw || null, send_invite: invite });
      toast.success(pw ? t("passwordReset") : t("setupLinkSent")); inviteToast(t, data.invite); setPw(""); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" aria-describedby={undefined} data-testid="reset-password-dialog">
        <DialogHeader><DialogTitle className="font-heading">{t("resetPassword")}</DialogTitle></DialogHeader>
        {target && (
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">{target.display_name} · {target.email}</div>
            <div className="space-y-1"><Label>{t("newPassword")} <span className="text-muted-foreground">({invite ? t("optionalWithLink") : t("passwordMinHint")})</span></Label>
              <Input type="password" data-testid="reset-password-input" value={pw} onChange={(e) => setPw(e.target.value)} /></div>
            <label className="flex items-center gap-2 text-sm">
              <Switch data-testid="reset-invite-switch" checked={invite} onCheckedChange={setInvite} />{t("alsoSendInvite")}
            </label>
            <p className="text-xs text-muted-foreground">{t("tempPasswordNote")}</p>
            <Button data-testid="reset-password-submit" className="w-full bg-[#1E3A8A] hover:bg-[#1c3479]" disabled={busy || (pw ? pw.length < 8 : !invite)} onClick={submit}>
              {busy ? "..." : pw ? t("resetPassword") : t("sendSetupLink")}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function UsersTab() {
  const { t } = useLang();
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [open, setOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetInvite, setResetInvite] = useState(false);
  const [form, setForm] = useState({ email: "", display_name: "", role: "TEAM_MEMBER", password: "", send_invite: true });
  const load = useCallback(() => api.get("/admin/users").then((r) => setUsers(r.data)).catch((e) => toast.error(apiError(e))), []);
  useEffect(() => { load(); }, [load]);
  if (!users) return <Skeleton className="h-40 w-full" />;
  const slug = (email) => email.split("@")[0];
  return (
    <div className="space-y-4">
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button data-testid="admin-add-user-btn" className="bg-[#1E3A8A] hover:bg-[#1c3479]">{t("addUser")}</Button>
        </DialogTrigger>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">{t("addUser")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>{t("email")}</Label>
              <Input data-testid="admin-user-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="space-y-1"><Label>{t("name")}</Label>
              <Input data-testid="admin-user-name-input" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} /></div>
            <div className="space-y-1"><Label>{t("role")}</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="admin-user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["TEAM_MEMBER", "PM", "LEAD", "EXECUTIVE", "ADMIN", "AUDITOR"].map((r) => <SelectItem key={r} value={r} data-testid={`admin-user-role-option-${r}`}>{r}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div className="space-y-1"><Label>{t("initialPassword")} <span className="text-muted-foreground">({form.send_invite ? t("optionalWithLink") : t("passwordMinHint")})</span></Label>
              <Input type="password" data-testid="admin-user-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            <label className="flex items-center gap-2 text-sm">
              <Switch data-testid="admin-user-invite-switch" checked={form.send_invite} onCheckedChange={(v) => setForm({ ...form, send_invite: v })} />{t("sendInvite")}
            </label>
            <p className="text-xs text-muted-foreground">{t("tempPasswordNote")}</p>
            <Button data-testid="admin-user-create-btn" className="w-full bg-[#1E3A8A] hover:bg-[#1c3479]"
              disabled={!form.email || !form.display_name || (form.password ? form.password.length < 8 : !form.send_invite)}
              onClick={async () => {
                try {
                  const { data } = await api.post("/admin/users", { ...form, password: form.password || null });
                  toast.success("User created"); inviteToast(t, data.invite); setOpen(false);
                  setForm({ email: "", display_name: "", role: "TEAM_MEMBER", password: "", send_invite: true }); load();
                } catch (e) { toast.error(apiError(e)); }
              }}>{t("addUser")}</Button>
          </div>
        </DialogContent>
      </Dialog>
      <div className="apha-card overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>{t("name")}</TableHead><TableHead>{t("email")}</TableHead><TableHead>{t("role")}</TableHead><TableHead>{t("statusLabel")}</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`admin-user-row-${slug(u.email)}`}>
                <TableCell className="font-medium">{u.display_name}{u.id === me.id && <span className="ml-1.5 text-[10px] text-muted-foreground">(you)</span>}</TableCell>
                <TableCell className="text-muted-foreground">
                  {u.email}
                  {u.invite_status && (
                    <div className="text-[10px]" data-testid={`admin-user-invite-status-${slug(u.email)}`}>{t("invitedOn")} {u.invite_sent_at?.slice(0, 10)} · {u.invite_status}</div>
                  )}
                </TableCell>
                <TableCell>{u.role}</TableCell>
                <TableCell>{u.status === "ACTIVE" ? t("active") : t("inactive")}</TableCell>
                <TableCell className="whitespace-nowrap">
                  <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid={`admin-user-reset-${slug(u.email)}`} onClick={() => { setResetInvite(false); setResetTarget(u); }}>
                    <KeyRound className="h-3.5 w-3.5 mr-1" />{t("resetPassword")}
                  </Button>
                  {u.id !== me.id && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid={`admin-user-invite-${slug(u.email)}`} onClick={() => { setResetInvite(true); setResetTarget(u); }}>
                      <Mail className="h-3.5 w-3.5 mr-1" />{t("resendInvite")}
                    </Button>
                  )}
                  {u.id !== me.id && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid={`admin-user-toggle-${slug(u.email)}`}
                      onClick={async () => {
                        try {
                          await api.patch(`/admin/users/${u.id}`, { status: u.status === "ACTIVE" ? "INACTIVE" : "ACTIVE" });
                          load();
                        } catch (e) { toast.error(apiError(e)); }
                      }}>
                      {u.status === "ACTIVE" ? t("inactive") : t("active")}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <ResetPasswordDialog target={resetTarget} withInvite={resetInvite} onClose={() => setResetTarget(null)} />
    </div>
  );
}

function HolidaysTab() {
  const { t } = useLang();
  const [holidays, setHolidays] = useState(null);
  const [form, setForm] = useState({ holiday_date: "", name: "" });
  const load = useCallback(() => api.get("/admin/holidays").then((r) => setHolidays(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!holidays) return <Skeleton className="h-40 w-full" />;
  return (
    <div className="space-y-4">
      <div className="flex gap-2 items-end">
        <div className="space-y-1"><Label>Date</Label>
          <Input type="date" data-testid="holiday-date-input" value={form.holiday_date} onChange={(e) => setForm({ ...form, holiday_date: e.target.value })} /></div>
        <div className="space-y-1 flex-1 max-w-xs"><Label>Name</Label>
          <Input data-testid="holiday-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
        <Button data-testid="holiday-add-btn" className="bg-[#1E3A8A] hover:bg-[#1c3479]" disabled={!form.holiday_date || !form.name}
          onClick={async () => {
            try { await api.post("/admin/holidays", form); toast.success(t("addHoliday")); setForm({ holiday_date: "", name: "" }); load(); }
            catch (e) { toast.error(apiError(e)); }
          }}>{t("addHoliday")}</Button>
      </div>
      <div className="apha-card divide-y">
        {holidays.map((h) => (
          <div key={h.id} className="p-3 text-sm flex gap-4"><span className="font-medium">{h.holiday_date}</span>{h.name}</div>
        ))}
        {holidays.length === 0 && <div className="p-4 text-sm text-muted-foreground">{t("noData")}</div>}
      </div>
    </div>
  );
}

function DangerZone() {
  const { t } = useLang();
  const [status, setStatus] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/demo-data").then((r) => setStatus(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!status) return null;
  const purge = async () => {
    setBusy(true);
    try {
      await api.post("/admin/demo-data/purge", { confirm });
      toast.success(t("purgeDemoDone")); setConfirm(""); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="apha-card p-4 border-[#FECACA]" data-testid="purge-demo-card">
      <div className="flex items-center gap-2 font-heading font-semibold text-sm text-[#991B1B]">
        <AlertTriangle className="h-4 w-4" />{t("dangerZone")} · {t("purgeDemoTitle")}
      </div>
      {!status.present ? (
        <p className="text-sm text-muted-foreground mt-2" data-testid="demo-clean-note">{t("noDemoData")}</p>
      ) : (
        <div className="mt-2 space-y-3">
          <p className="text-sm text-muted-foreground">{t("purgeDemoHint")}</p>
          <div className="text-xs text-muted-foreground" data-testid="demo-data-counts">
            {status.demo_users} demo users · {status.projects} projects · {status.updates} updates · {status.warnings} warnings
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1"><Label>{t("purgeDemoConfirmLabel")}</Label>
              <Input data-testid="purge-demo-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="w-48" /></div>
            <Button variant="destructive" data-testid="purge-demo-btn" disabled={confirm !== "PURGE" || busy} onClick={purge}>
              {busy ? "..." : t("purgeDemoButton")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function IntegrationsTab({ isAdmin }) {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => api.get("/admin/integration-health").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Skeleton className="h-40 w-full" />;

  const run = async (key, fn) => {
    setBusy(key);
    try { const r = await fn(); toast.success(JSON.stringify(r.data.status || r.data)); load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  const statusColor = (s) =>
    s === "CONNECTED" || s === "OK" ? "text-[#166534] bg-[#DCFCE7] border-[#BBF7D0]"
      : s === "DEGRADED" ? "text-[#9A3412] bg-[#FFEDD5] border-[#FED7AA]"
      : "text-[#334155] bg-[#F1F5F9] border-[#E2E8F0]";

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {data.integrations.map((i) => (
          <div key={i.key} className="apha-card p-4" data-testid={`integration-card-${i.key}`}>
            <div className="flex items-center justify-between">
              <span className="font-heading font-semibold text-sm uppercase">{i.key}</span>
              <span className={`text-[10px] font-semibold border rounded px-1.5 py-0.5 ${statusColor(i.status)}`}>{i.status}</span>
            </div>
            <div className="text-xs text-muted-foreground mt-1.5">{i.detail}</div>
            {i.last_error && <div className="text-[11px] text-[#991B1B] mt-1 break-words">{i.last_error}</div>}
            <div className="text-[10px] text-muted-foreground mt-1.5">Updated: {new Date(i.updated_at).toLocaleString()}</div>
            {isAdmin && i.key === "jira" && (
              <Button size="sm" variant="outline" className="mt-2 h-7 text-xs" data-testid="test-jira-btn" disabled={busy === "jira"}
                onClick={() => run("jira", () => api.post("/admin/integrations/jira/test"))}>
                <RefreshCw className={`h-3 w-3 mr-1 ${busy === "jira" ? "animate-spin" : ""}`} />{t("testConnection")}
              </Button>
            )}
            {isAdmin && i.key === "ai" && (
              <Button size="sm" variant="outline" className="mt-2 h-7 text-xs" data-testid="test-ai-btn" disabled={busy === "ai"}
                onClick={() => run("ai", () => api.post("/admin/integrations/ai/test"))}>
                <RefreshCw className={`h-3 w-3 mr-1 ${busy === "ai" ? "animate-spin" : ""}`} />{t("testConnection")}
              </Button>
            )}
          </div>
        ))}
      </div>
      <div className="apha-card p-4 flex flex-wrap items-center gap-4">
        <div className="text-sm space-x-4">
          <span>Notifications: <b>{data.metrics.notifications_sent}</b></span>
          <span>AI summaries: <b>{data.metrics.ai_summaries_generated}</b></span>
          <span>Rules-only: <b>{data.metrics.rules_only_summaries}</b></span>
          <span>Jira issues: <b>{data.metrics.jira_issues_synced}</b></span>
        </div>
        {isAdmin && (
          <Button size="sm" data-testid="run-daily-cycle-btn" className="bg-[#1E3A8A] hover:bg-[#1c3479] ml-auto" disabled={busy === "cycle"}
            onClick={() => run("cycle", () => api.post("/admin/run-daily-cycle"))}>
            <Play className="h-3.5 w-3.5 mr-1.5" />{t("runDailyCycle")}
          </Button>
        )}
      </div>
      {isAdmin && <DangerZone />}
    </div>
  );
}

function AuditTab() {
  const { t } = useLang();
  const [events, setEvents] = useState(null);
  useEffect(() => { api.get("/admin/audit-events").then((r) => setEvents(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  if (!events) return <Skeleton className="h-40 w-full" />;
  return (
    <div className="apha-card overflow-x-auto">
      <Table>
        <TableHeader><TableRow>
          <TableHead>Time</TableHead><TableHead>Actor</TableHead><TableHead>Action</TableHead><TableHead>Entity</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {events.map((e) => (
            <TableRow key={e.id}>
              <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{new Date(e.occurred_at).toLocaleString()}</TableCell>
              <TableCell className="text-sm">{e.actor_name}</TableCell>
              <TableCell className="text-sm font-medium">{e.action}</TableCell>
              <TableCell className="text-xs text-muted-foreground">{e.entity_type} · {String(e.entity_id).slice(0, 8)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default function Admin() {
  const { t } = useLang();
  const { user } = useAuth();
  const isAdmin = user.role === "ADMIN";
  return (
    <div className="space-y-6" data-testid="admin-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">{t("admin")}</h1>
      <Tabs defaultValue={isAdmin ? "users" : "integrations"}>
        <TabsList className="flex-wrap h-auto">
          {isAdmin && <TabsTrigger value="users" data-testid="admin-tab-users">{t("users")}</TabsTrigger>}
          {isAdmin && <TabsTrigger value="projects" data-testid="admin-tab-projects">{t("projects")}</TabsTrigger>}
          {isAdmin && <TabsTrigger value="holidays" data-testid="admin-tab-holidays">{t("holidays")}</TabsTrigger>}
          <TabsTrigger value="config" data-testid="admin-tab-config">{t("configuration")}</TabsTrigger>
          {isAdmin && <TabsTrigger value="email" data-testid="admin-tab-email">{t("emailDigest")}</TabsTrigger>}
          {isAdmin && <TabsTrigger value="jira" data-testid="admin-tab-jira">{t("jiraTab")}</TabsTrigger>}
          {isAdmin && <TabsTrigger value="gate" data-testid="admin-tab-gate">{t("accessGate")}</TabsTrigger>}
          <TabsTrigger value="integrations" data-testid="admin-tab-integrations">{t("integrations")}</TabsTrigger>
          <TabsTrigger value="audit" data-testid="admin-tab-audit">{t("auditLog")}</TabsTrigger>
        </TabsList>
        {isAdmin && <TabsContent value="users" className="mt-4"><UsersTab /></TabsContent>}
        {isAdmin && <TabsContent value="projects" className="mt-4"><AdminProjects /></TabsContent>}
        {isAdmin && <TabsContent value="holidays" className="mt-4"><HolidaysTab /></TabsContent>}
        <TabsContent value="config" className="mt-4"><AdminConfig isAdmin={isAdmin} /></TabsContent>
        {isAdmin && <TabsContent value="email" className="mt-4"><AdminEmail /></TabsContent>}
        {isAdmin && <TabsContent value="jira" className="mt-4"><AdminJira /></TabsContent>}
        {isAdmin && <TabsContent value="gate" className="mt-4"><AdminGate /></TabsContent>}
        <TabsContent value="integrations" className="mt-4"><IntegrationsTab isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="audit" className="mt-4"><AuditTab /></TabsContent>
      </Tabs>
    </div>
  );
}
